import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast, Toaster } from "sonner";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { ScaleLoader } from "react-spinners";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useGetInvitesByStatus } from "features/home/hooks/useGetInvitesByStatus";
import { routePath } from "config/routeMgt/routePath";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { IFeedbackJob } from "../../feedback/types";
import { useGetPendingFeedbackJobs } from "features/feedback/hooks/useGetPendingFeedbackJobs";
import FeedbackStatusScreen from "../components/FeedbackStatusScreen";

export const RecruierInvite = () => {
  const navigate = useNavigate();
  const { status } = useParams<{
    status: "accepted" | "pending" | "rejected" | "feedback";
  }>();
  const { userInfo } = useGetUserInfo();
  const [activeTab, setActiveTab] = useState<
    "pending" | "accepted" | "rejected" | "feedback"
  >("pending");

  const [selectedJobForFeedback, setSelectedJobForFeedback] =
    useState<IFeedbackJob | null>(null);
  const [showStatusScreen, setShowStatusScreen] = useState(false);

  const validStatuses = [
    "pending",
    "accepted",
    "rejected",
    "feedback",
  ] as const;

  useEffect(() => {
    if (!status || !validStatuses.includes(status as any)) {
      navigate(`${routePath.recruierInvites}/pending`, { replace: true });
    } else {
      setActiveTab(status as (typeof validStatuses)[number]);
    }
  }, [status]);

  const { data, isLoading } = useGetInvitesByStatus(
    activeTab === "feedback" ? "rejected" : activeTab,
  );

  const { data: feedbackJobsData } = useGetPendingFeedbackJobs({});
  const feedbackInvites: IFeedbackJob[] = (feedbackJobsData ||
    []) as IFeedbackJob[];

  const [searchQuery, setSearchQuery] = useState("");
  const invitesData = Array.isArray(data) ? data : [];
  const showLoader = isLoading;
  const pendingInvites = Array.isArray(invitesData) 
  ? invitesData.filter((invite) => invite.status === 0)
  : [];

const progressedInvites = Array.isArray(invitesData)
  ? invitesData.filter((invite) => invite.status === 1)
  : [];

const declinedInvites = Array.isArray(invitesData)
  ? invitesData.filter((invite) => invite.status === 2)
  : [];

  const filterBySearch = (invites: any[]) => {
      if (!Array.isArray(invites)) return [];
    if (!searchQuery.trim()) return invites;

    const query = searchQuery.toLowerCase();
    return invites.filter((invite) => {
      if (activeTab === "feedback") {
        return (
          invite.title?.toLowerCase().includes(query) ||
          invite.company_name?.toLowerCase().includes(query) ||
          invite.level?.toLowerCase().includes(query)
        );
      }
      return (
        invite.listing?.title?.toLowerCase().includes(query) ||
        invite.listing?.company_name?.toLowerCase().includes(query) ||
        invite.listing?.location_city?.toLowerCase().includes(query) ||
        invite.listing?.type?.toLowerCase().includes(query) ||
        invite.listing?.description?.toLowerCase().includes(query) ||
        invite.talent?.first_name?.toLowerCase().includes(query) ||
        invite.talent?.last_name?.toLowerCase().includes(query) ||
        invite.talent?.middle_name?.toLowerCase().includes(query) ||
        `${invite.talent?.first_name || ""} ${invite.talent?.middle_name || ""} ${invite.talent?.last_name || ""}`
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim()
          .includes(query)
      );
    });
  };

  useEffect(() => {
    if (status) {
      setActiveTab(status);
    }
  }, [status]);

  const filteredPendingInvites = useMemo(
    () => filterBySearch(pendingInvites),
    [pendingInvites, searchQuery],
  );

  const filteredProgressedInvites = useMemo(
    () => filterBySearch(progressedInvites),
    [progressedInvites, searchQuery],
  );

  const filteredDeclinedInvites = useMemo(
    () => filterBySearch(declinedInvites),
    [declinedInvites, searchQuery],
  );

  const filteredFeedbackInvites = useMemo(
    () => filterBySearch(feedbackInvites),
    [feedbackInvites, searchQuery],
  );

  const getActiveTabData = () => {
    switch (activeTab) {
      case "pending":
        return filteredPendingInvites;
      case "accepted":
        return filteredProgressedInvites;
      case "rejected":
        return filteredDeclinedInvites;
      case "feedback":
        return filteredFeedbackInvites;
      default:
        return [];
    }
  };

  const activeTabData = getActiveTabData();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleGiveFeedback = (job: IFeedbackJob) => {
    setSelectedJobForFeedback(job);
    setShowStatusScreen(true);
  };

  const handleBackFromStatus = () => {
    setShowStatusScreen(false);
    setSelectedJobForFeedback(null);
  };

  const handleCompleteStatus = () => {
    toast.success("Feedback status submitted successfully!");
    setShowStatusScreen(false);
    setSelectedJobForFeedback(null);
  };

  if (showStatusScreen && selectedJobForFeedback) {
    return (
      <DashboardLayout>
        <Navbar
          title="Recruitment Feedback"
          description="Provide feedback on recruitment process"
        />
        <FeedbackStatusScreen
          job={selectedJobForFeedback}
          onBack={handleBackFromStatus}
          onComplete={handleCompleteStatus}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors closeButton />
      <Navbar
        description={
          userInfo?.current_team?.name || "View available Recruiter Invites"
        }
        title="Recruiter Invites"
      />

      <div className="flex gap-6 mt-6">
        <div className="w-56 bg-white border border-gray-200 rounded-xl p-4 space-y-2">
          {[
            { id: "pending", label: "Pending", count: pendingInvites.length },
            {
              id: "accepted",
              label: "Accepted",
              count: progressedInvites.length,
            },
            {
              id: "rejected",
              label: "Declined",
              count: declinedInvites.length,
            },
            {
              id: "feedback",
              label: "Feedback",
              count: feedbackInvites.length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as any);
                navigate(`${routePath.recruierInvites}/${tab.id}`, {
                  replace: true,
                });
              }}
              disabled={showLoader}
              className={`w-full text-left py-2 px-3 rounded-lg flex items-center justify-between ${activeTab === tab.id
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
                } ${showLoader ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <span>{tab.label}</span>
              <div className="flex items-center gap-2">
                {showLoader && activeTab === tab.id && (
                  <ScaleLoader
                    color="var(--app-color-primary)"
                    height={15}
                    width={2}
                    margin={1}
                  />
                )}
                {activeTab === tab.id && (
                  <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {tab.count}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="flex justify-between items-center bg-white border border-gray-200 rounded-xl px-4 py-3 mb-6 shadow-sm">
            <div className="relative w-96">
              <i className="ri-search-line absolute left-3 top-3 text-gray-500 text-lg"></i>
              <input
                type="text"
                placeholder={
                  activeTab === "feedback"
                    ? "Search closed jobs..."
                    : "Search by name or job title"
                }
                value={searchQuery}
                onChange={handleSearchChange}
                disabled={showLoader}
                className={`w-full border border-gray-200 rounded-lg pl-10 pr-10 py-2 focus:outline-none ${showLoader ? "bg-gray-50 cursor-not-allowed" : ""
                  }`}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
            {activeTab === "feedback" && feedbackInvites.length > 0 && (
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg">
                <i className="ri-feedback-line text-blue-500"></i>
                <span className="text-sm text-blue-700">
                  {feedbackInvites.length} job
                  {feedbackInvites.length !== 1 ? "s" : ""} need feedback
                </span>
              </div>
            )}
          </div>

          {/* Invites List */}
          <div className="mt-8 max-h-[calc(100vh-260px)] overflow-y-auto pr-2">
            {showLoader ? (
              <div className="flex justify-center items-center py-20">
                <ScaleLoader
                  color="var(--app-color-primary)"
                  height={30}
                  width={3}
                  margin={2}
                />
              </div>
            ) : activeTabData.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {activeTabData.map((invite) =>
                  activeTab === "feedback" ? (
                    <FeedbackCard
                      key={invite.id}
                      job={invite as IFeedbackJob}
                      onGiveFeedback={handleGiveFeedback}
                    />
                  ) : (
                    <InviteCard
                      key={invite.id}
                      invite={invite}
                      tab={activeTab}
                    />
                  ),
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {searchQuery
                      ? `No ${activeTab} items found for "${searchQuery}"`
                      : activeTab === "feedback"
                        ? "No Closed Jobs Requiring Feedback"
                        : `No ${activeTab} recruiter invites`}
                  </h3>
                  <p className="text-gray-500">
                    {searchQuery
                      ? "Try searching with different keywords"
                      : activeTab === "pending"
                        ? "You don't have any pending recruiter invites"
                        : activeTab === "accepted"
                          ? "You haven't accepted any recruiter invites yet"
                          : activeTab === "rejected"
                            ? "You haven't declined any recruiter invites"
                            : activeTab === "feedback"
                              ? "All feedback has been provided for closed jobs"
                              : "You don't have any job offers"}
                  </p>
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const InviteCard = ({ invite, tab }: { invite: any; tab: string }) => {
  const [selectedDateId, setSelectedDateId] = useState<number | null>(
    invite.dates?.[0]?.id || null,
  );
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const handleDateSelect = (dateId: number) => {
    setSelectedDateId(dateId);
  };

  const getStatusBadge = () => {
    switch (tab) {
      case "pending":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            Pending
          </span>
        );
      case "accepted":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
            Accepted
          </span>
        );
      case "rejected":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
            Declined
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="ri-briefcase-line text-xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {invite.talent?.first_name} {invite.talent?.last_name}{" "}
                  {invite.talent?.middle_name}
                </h3>
                <h4 className="text-md font-semibold text-gray-900">
                  {invite.listing?.title}
                </h4>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-building-line mr-1"></i>
                    {invite.listing?.company_name}
                  </span>
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-map-pin-line mr-1"></i>
                    {invite.listing?.location_city}
                  </span>
                  <span className="text-sm text-gray-600 flex items-center">
                    <i className="ri-briefcase-line mr-1"></i>
                    {invite.listing?.type}
                  </span>
                  {getStatusBadge()}
                </div>
              </div>
            </div>

            <div className="ml-15 pl-1">
              <p className="text-sm text-gray-600 mb-3">
                {invite.listing?.description
                  ?.replace(/<[^>]*>/g, "")
                  ?.substring(0, 150)}
                ...
              </p>

              {invite.dates && invite.dates.length > 0 && tab === "pending" && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Interview Date:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {invite.dates.map((dateItem: any) => (
                      <button
                        key={dateItem.id}
                        type="button"
                        onClick={() => handleDateSelect(dateItem.id)}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${selectedDateId === dateItem.id
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        {dayjs(dateItem.date).format("DD/MM/YYYY")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-4 min-w-[180px]">
            <div className="text-right">
              <p className="text-xs text-gray-500">Invite Sent</p>
              <p className="text-sm font-medium text-gray-900">
                {dayjs(invite.created_at).format("DD/MMM/YYYY")}
              </p>

              {tab === "accepted" && (
                <>
                  <p className="text-xs text-gray-500 mt-2">Invite Accepted</p>
                  <p className="text-sm font-medium text-gray-900">
                    {dayjs(invite.updated_at).format("DD/MMM/YYYY")}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FeedbackCard = ({
  job,
  onGiveFeedback,
}: {
  job: IFeedbackJob;
  onGiveFeedback: (job: IFeedbackJob) => void;
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-feedback-line text-xl text-blue-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {job.title}
              </h3>
              <p className="text-sm text-gray-600">{job.company_name}</p>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className="text-sm text-gray-600 flex items-center">
                  <i className="ri-briefcase-line mr-1"></i>
                  {job.level?.replace(/_/g, " ") || "N/A"}
                </span>
                <span className="text-sm text-gray-600 flex items-center">
                  <i className="ri-file-list-line mr-1"></i>
                  {job.applications_count || 0} applications
                </span>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  Needs Feedback
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3 ml-13">
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-gray-500">Posted:</span>
                <span className="ml-1 font-medium">
                  {formatDate(job.created_at)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Closed:</span>
                <span className="ml-1 font-medium">
                  {formatDate(job.closed_at || job.updated_at)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 ml-4">
          <div className="text-right">
            <p className="text-xs text-gray-500">Closed Job</p>
            <p className="text-sm font-medium text-gray-900">
              {dayjs(job.closed_at || job.updated_at).format("DD/MMM/YYYY")}
            </p>
          </div>

          <button
            onClick={() => onGiveFeedback(job)}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          >
            <i className="ri-feedback-line"></i>
            Give Feedback
          </button>
        </div>
      </div>
    </div>
  );
};
